import { useCallback, useRef } from "react"
import noop from "lib/noop"
import { clone } from "lib/clone"
import { useBoundContext } from "lib/@components/binding/use-bound-context"

/**
 * A tailoring payload is a delta store: a `frequency` on a payload task means "this value,
 * forever"; its absence means "inherit from the schedule version the plan is anchored to". So the
 * editor must never write a frequency the user did not enter — not even one it means to clean up
 * later, because it renders for EVERY task row while edit mode is on (`Editing`/`useEditable`), and
 * any save landing in the meantime persists whatever the payload holds.
 *
 * The editor still has to SHOW the inherited frequency: the period picker cannot render without a
 * period, and opening blank would lose the schedule's, so a user typing an interval would silently
 * get the default period instead. So the inputs bind to a DRAFT — the payload override if there is
 * one, otherwise a copy of what the task inherits — and `commit` copies the draft into the payload
 * only once the user has moved something, removing the override again when the draft comes back to
 * the inherited value.
 *
 * The inherited value is captured ONCE, while the payload has no override, and never re-read:
 * extend-schedule.mjs assigns the payload's frequency into the extended task BY REFERENCE, so a
 * live `task.frequency` can be the very object the user is editing, which would read as "unmoved"
 * whatever they typed. A value captured while there was no override cannot be aliased to one.
 *
 * In the master editors (`target === task`: authoring add/edit task, the 0U wizard, the bulk
 * frequency dialog) there is no delta store and nothing to inherit from — those surfaces read
 * `target.frequency` straight back — so the draft IS the target and every write lands on the task
 * directly, exactly as before.
 */
export function useFrequencyOverride() {
    const { task, target, onChange = noop } = useBoundContext()
    const tailoring = !!target && !!task && target !== task
    // One ref, so `markEdited` and `commit` can keep a stable identity for the editor's effect
    // dependencies while still reading the CURRENT payload target and change handler.
    const state = useRef({ draft: null, inherited: null, edited: false })
    Object.assign(state.current, { tailoring, target, onChange })
    if (tailoring && !state.current.draft) {
        state.current.inherited = !target.frequency && task.frequency ? clone(task.frequency) : null
        state.current.draft = { frequency: clone(target.frequency ?? task.frequency) ?? {} }
        // The draft's built form at capture. `commit` writes nothing while the built draft still
        // equals this, because "edited" alone is not evidence of a change: the combo fires its
        // onChanged when the menu merely OPENS, and for a task with a pre-existing override whose
        // stored shape differs from the rebuilt one (a missing derived code, an inherited label),
        // the old shape comparison called that a change and SAVED mid-click — remounting the
        // select and closing the menu under the user.
        state.current.baseline = buildFrequencyOverride(state.current.draft.frequency)
    }
    const markEdited = useCallback(markFrequencyEdited, [])
    const commit = useCallback(commitFrequency, [])
    return { editTarget: tailoring ? state.current.draft : target, markEdited, commit, tailoring }

    function markFrequencyEdited() {
        state.current.edited = true
    }

    function commitFrequency() {
        const { tailoring, target, onChange, draft, inherited, edited, baseline } = state.current
        if (!tailoring || !edited) return
        const frequency = buildFrequencyOverride(draft.frequency)
        // An EXISTING override, unmoved from what the row opened with: nothing to persist,
        // whatever shape the stored override had. Without this, "edited" set by a menu-open would
        // rewrite (and save) the override just to normalise its shape. Only when an override
        // existed at capture (`inherited` null) - a row with no override returning to the
        // inherited value must still fall through to the delete branch below.
        if (!inherited && isSameFrequency(baseline, frequency)) return
        if (isInheritedFrequency(inherited, frequency)) {
            if (!("frequency" in target)) return
            delete target.frequency
            onChange()
            return
        }
        if (isSameFrequency(target.frequency, frequency)) return
        target.frequency = frequency
        onChange()
    }
}

// A frequency is flat (period, interval, the derived code, and whatever else it inherited), so a
// key-wise compare is exact. `lib/isEqual` would be the house idiom, but it normalizes through the
// app's Array prototype extensions, which a unit test does not install.
function isSameFrequency(current, wanted) {
    if (!current) return false
    const keys = Object.keys(wanted)
    if (Object.keys(current).length !== keys.length) return false
    return keys.every((key) => current[key] === wanted[key])
}

/**
 * The override to persist for a draft the user has moved: every field the draft carries (so an
 * inherited `label` and anything else survives) with the code re-derived, so the code can never
 * disagree with the interval and period it is written beside.
 *
 * A blank interval is only ever a half-typed field — the settled value for an empty field is 0
 * (`handleBlur` in frequency-fields.js) — so persist 0 rather than an empty string that consumers
 * such as `periodDuration` would divide by.
 */
export function buildFrequencyOverride(frequency = {}) {
    const interval = frequency.interval === "" || frequency.interval == null ? 0 : frequency.interval
    return { ...frequency, interval, code: frequencyCode(interval, frequency.period) }
}

/**
 * True when `frequency` still says what the task inherits, so there is nothing to override. Period
 * and interval only: those are the fields assess-status compares, and the code is derived from
 * them. The interval is compared numerically because the text field yields strings.
 */
export function isInheritedFrequency(inherited, frequency) {
    if (!inherited) return false
    return frequency?.period === inherited.period && +frequency?.interval === +inherited.interval
}

export function frequencyCode(interval, period) {
    const periodChar = period === "Unknown" ? "U" : period?.[0] || "U"
    return `${interval}${periodChar}`
}
